/**
 * Installs a global `unhandledrejection` handler that suppresses
 * non-informative promise rejections — i.e. rejections whose value is NOT an
 * `Error` (DOM `Event`/`CustomEvent` objects, empty objects, primitives).
 *
 * Why: during the Auth0 `/auth-callback` flow, the silent-token / network
 * machinery occasionally rejects promises with raw DOM events or empty objects
 * instead of `Error` instances. These reach `window` as `unhandledrejection`
 * and, because they have no `.message` or `.stack`, Sentry captures them as
 * `<unknown>` / "Object captured as promise rejection" with no stacktrace —
 * pure noise (see Sentry issues WEB-9W and WEB-9V).
 *
 * The *real* auth failures are already handled by `AuthCallback.tsx`, which
 * logs them to Sentry with proper context. This handler only filters out the
 * duplicate, non-informative rejections. Genuine `Error` rejections (with a
 * stack) are left untouched so Sentry still reports them normally.
 *
 * Must be installed before Sentry's own GlobalHandlers integration attaches, so
 * we call this at the very top of `index.tsx`.
 */

import * as Sentry from '@sentry/react';

export const suppressNonErrorRejections = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;

    // Only swallow rejections that are NOT real Errors. Real Errors carry a
    // message + stack and should always be reported.
    // Note: this filter is intentionally global — any non-Error rejection from
    // any part of the app (not just Auth0) will be suppressed. If new code
    // starts rejecting with structured plain objects that carry useful debug
    // info, add an exemption here (e.g. check for a `.message` property).
    if (reason instanceof Error) {
      return;
    }

    // Log the suppression for debugging visibility - we need to know when
    // non-Error rejections occur to verify our assumptions and detect new patterns.
    console.warn(
      '[suppressNonErrorRejections] Suppressing non-Error rejection:',
      {
        reason,
        type: typeof reason,
        constructor: reason?.constructor?.name,
      },
    );

    // Add a Sentry breadcrumb for visibility without creating a full event.
    // This helps debug issues without polluting Sentry with noise.
    // Safe to call before Sentry.init() - it's a no-op when no client is bound.
    Sentry.addBreadcrumb({
      category: 'promise',
      message: 'Suppressed non-Error rejection',
      level: 'info',
      data: {
        reasonType: typeof reason,
        reasonConstructor: reason?.constructor?.name,
      },
    });

    // Prevent the rejection from propagating to Sentry's GlobalHandlers
    // (which would capture it as `<unknown>` with no stacktrace).
    event.preventDefault();
  });
};
